import React, {
  ReactNode,
  Fragment,
  useState,
  useEffect,
  useContext,
  useRef,
} from 'react'
import { Dialog, Transition } from '@headlessui/react'
import {
  Bars3Icon,
  CogIcon,
  DocumentIcon,
  InboxIcon,
  UserIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import Link from 'next/link'
import Image from 'next/image'
import { Switch } from '@headlessui/react'
import { captureException } from '@sentry/nextjs'

import { classNames } from 'app/utils/classes'
import { Footer, FooterProps } from './Footer'
import { Header, HeaderProps } from './Header'
import { Auth } from 'aws-amplify'
import { useTranslation } from 'next-i18next'
import { Cart, Event, Product, User } from 'app/types/authorization'
import { CompanyLogo, ProfileImage, StaticImage } from './Image'
import {
  BuildingStorefrontIcon,
  HomeIcon,
  ShoppingBagIcon,
  TicketIcon,
  UsersIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronDownIcon,
  QrCodeIcon,
  HomeModernIcon,
  EllipsisHorizontalIcon,
  ArrowTopRightOnSquareIcon,
  KeyIcon,
  ArrowLeftOnRectangleIcon,
  BuildingOfficeIcon,
  CalendarDaysIcon,
  BanknotesIcon,
  ShoppingCartIcon,
  QuestionMarkCircleIcon,
  ListBulletIcon,
} from '@heroicons/react/20/solid'
import { StandardAPIRequest } from 'app/types/api'
import { clientGetIdToken } from 'app/utils/cognito'
import { Arena } from 'app/types/arena'
import { CompanyProfile, Organization } from 'app/types/organization'
import { capitalize } from 'app/utils/text'
import { AuthContext } from 'app/utils/context'
import { AuthDispatchTypes } from 'app/utils/reducers'
import { SeasonTicket } from 'app/types/season_ticket'
import useOutsideClick from 'app/utils/outsideClick'
import PoweredByLiveday from './icons/PoweredByLiveday'
import { Membership } from 'app/types/membership'
import { VenueCart } from './Cart'

type LayoutProps = {
  hideFooter?: boolean
  hideHeader?: boolean
  children: ReactNode
} & HeaderProps &
  FooterProps

function Layout({
  children,
  user,
  companyProfile,
  organizationId,
  hideFooter = false,
  hideHeader = false,
  ...props
}: LayoutProps) {
  return (
    <div>
      {!hideHeader && (
        <Header
          companyProfile={companyProfile}
          user={user}
          organizationId={organizationId}
          {...props}
        />
      )}
      <main>{children}</main>
      {!hideFooter && (
        <Footer
          organizationId={organizationId}
          companyProfile={companyProfile}
          {...props}
        />
      )}
    </div>
  )
}

interface AuthLayoutProps {
  children: React.ReactNode
}

function AuthLayout({ children }: AuthLayoutProps) {
  return (
    <>
      <div className="relative flex min-h-full justify-center md:px-12 lg:px-0">
        <div className="relative z-10 flex flex-1 flex-col bg-white px-4 py-10 shadow-2xl sm:justify-center md:flex-none md:px-28">
          <div className="mx-auto w-full max-w-md sm:px-4 md:w-96 md:max-w-sm md:px-0">
            {children}
          </div>
        </div>
        <div className="hidden sm:contents lg:relative lg:block lg:flex-1">
          <Image
            className="absolute inset-0 h-full w-full object-cover"
            src={'/images/falkenberg/overlay.jpg'}
            alt=""
            unoptimized
          />
        </div>
      </div>
    </>
  )
}

interface Step {
  id: number
  blocked?: boolean
  name: string
  description: string
}

interface VenueLayoutStepsProps {
  eventId?: string
  seasonTicketId?: string
  shouldHaveTickets: boolean
  shouldHaveItems: boolean
  shouldHaveKiosk: boolean
  shouldHaveRestaurant: boolean
  currentStep: number
  cartItems: Product[] | undefined
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>
  setShowFailureMessage: React.Dispatch<React.SetStateAction<string>>
  companyProfile: CompanyProfile | null
}

function VenueLayoutSteps({
  eventId,
  seasonTicketId,
  shouldHaveTickets,
  shouldHaveItems,
  shouldHaveKiosk,
  shouldHaveRestaurant,
  currentStep,
  cartItems,
  setCurrentStep,
  setShowFailureMessage,
  companyProfile,
}: VenueLayoutStepsProps) {
  const { t } = useTranslation('common')
  const [steps, setSteps] = useState<Step[]>([])

  useEffect(() => {
    let localSteps: Step[] = []
    if (eventId && shouldHaveTickets) {
      localSteps.push({
        id: 1,
        name: t('tickets'),
        description: t('Find-your-next-tickets'),
      })
    }

    if (seasonTicketId && shouldHaveTickets) {
      localSteps.push({
        id: 1,
        name: t('season-tickets'),
        description: t('find-your-next-season-tickets'),
      })
    }

    if (shouldHaveRestaurant) {
      localSteps.push({
        id: localSteps.length + 1,
        name: t('restaurant'),
        description: t('explore-our-culinary-options'),
      })
    }

    if (shouldHaveKiosk) {
      localSteps.push({
        id: localSteps.length + 1,
        name: t('kiosk'),
        description: t('check-out-to-secure-your-tickets'),
      })
    }

    if (shouldHaveItems) {
      localSteps.push({
        id: localSteps.length + 1,
        name: t('merch'),
        description: t('view-addons-to-enhance-your-experience'),
      })
    }

    localSteps.push({
      id: localSteps.length + 1,
      blocked: cartItems?.length === 0,
      name: t('checkout'),
      description: t('check-out-to-secure-your-tickets'),
    })
    setSteps(localSteps)
  }, [
    eventId,
    seasonTicketId,
    t,
    cartItems,
    shouldHaveTickets,
    shouldHaveItems,
    shouldHaveKiosk,
    shouldHaveRestaurant,
  ])

  return (
    <>
      <div className="z-10 hidden bg-white py-16 shadow sm:block sm:py-6">
        <nav
          className="mx-auto flex h-full flex-col justify-center"
          aria-label="Progress"
        >
          <ol
            role="list"
            className="flex flex-row flex-wrap items-center justify-evenly gap-5 sm:flex-nowrap sm:gap-0"
          >
            {steps?.map((step) => {
              const isCompletedStep = step.id < currentStep

              return (
                <React.Fragment key={step.id}>
                  <li
                    className="cursor-pointer font-extrabold text-gray-800"
                    onClick={() => {
                      if (step.blocked !== true) {
                        setCurrentStep(step.id)
                      } else {
                        setShowFailureMessage(
                          t('cannot-proceed-to-payment-cart-is-empty') ?? ''
                        )
                      }
                    }}
                  >
                    <div className="flex flex-row">
                      <div className="step">
                        <div
                          className="mx-10 flex flex-row items-center gap-1 text-base font-bold leading-tight"
                          style={{
                            color:
                              step.id === currentStep &&
                              companyProfile?.primary_main &&
                              companyProfile?.primary_main !== '#FFFFFF' &&
                              companyProfile?.primary_main !== '#FFFEFE'
                                ? companyProfile?.primary_main
                                : undefined,
                          }}
                        >
                          {isCompletedStep && (
                            <CheckCircleIcon className="h-4 w-4" />
                          )}
                          {step.name}
                        </div>
                      </div>
                    </div>
                  </li>
                  {step.id !== steps.length && (
                    <div className="border-1 hidden h-1 w-full border-b border-gray-200 sm:inline-block sm:content-['']"></div>
                  )}
                </React.Fragment>
              )
            })}
          </ol>
        </nav>
      </div>
      <div className="z-10 flex w-screen flex-col bg-gray-50 py-2 shadow sm:hidden">
        <div className="flex w-full flex-row justify-center">
          <nav
            className="flex items-center justify-center"
            aria-label="Progress"
          >
            <ol role="list" className="flex items-center gap-x-5">
              {steps.map((step) => {
                const isCompletedStep = step.id < currentStep
                const isCurrentStep = step.id === currentStep

                return (
                  <li key={step.name}>
                    {isCompletedStep ? (
                      <div className="bg-brand block h-2.5 w-2.5 rounded-full">
                        <span className="sr-only">{step.name}</span>
                      </div>
                    ) : isCurrentStep ? (
                      <div
                        className="relative flex items-center justify-center"
                        aria-current="step"
                      >
                        <span
                          className="absolute flex h-5 w-5 p-px"
                          aria-hidden="true"
                        >
                          <span className="h-full w-full rounded-full bg-indigo-200" />
                        </span>
                        <span
                          className="bg-brand relative block h-2.5 w-2.5 rounded-full"
                          aria-hidden="true"
                        />
                        <span className="sr-only">{step.name}</span>
                      </div>
                    ) : (
                      <div className="block h-2.5 w-2.5 rounded-full bg-gray-200 hover:bg-gray-400">
                        <span className="sr-only">{step.name}</span>
                      </div>
                    )}
                  </li>
                )
              })}
            </ol>
          </nav>
        </div>
        {currentStep > 1 && (
          <div
            onClick={() => setCurrentStep((curr) => curr - 1)}
            className="flex cursor-pointer flex-row items-center pt-2"
          >
            <ChevronLeftIcon className="mr-0.5 h-6 w-6 text-gray-800" />
            <p className="text-gray-800">{t('back')}</p>
          </div>
        )}
      </div>
    </>
  )
}

interface VenueCustomerLayoutProps {
  children: React.ReactNode
  hideHeader?: boolean
  hideFooter?: boolean
  user: User | undefined
  event?: Event
  seasonTicket?: SeasonTicket
  membership?: Membership
  arena: Arena | null
  currentStep: number
  shouldHaveTickets: boolean
  shouldHaveItems: boolean
  shouldHaveKiosk: boolean
  shouldHaveRestaurant: boolean
  shouldHaveCart: boolean
  shouldShowSteps?: boolean
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>
  refetchCart: () => void
  refetchTickets: () => Promise<void>
  setShowSuccessMessage: React.Dispatch<React.SetStateAction<string>>
  setShowFailureMessage: React.Dispatch<React.SetStateAction<string>>
  cart: Cart | undefined
  companyProfile: CompanyProfile | null
  isRegister?: boolean
  organizationId: string | string[]
  ticketsInCart: string[]
  hideProceedButton?: boolean
}

function VenueCustomerLayout({
  user,
  children,
  hideFooter,
  event,
  seasonTicket,
  membership,
  arena,
  currentStep,
  shouldHaveTickets,
  shouldHaveItems,
  shouldHaveKiosk,
  shouldHaveRestaurant,
  shouldHaveCart,
  shouldShowSteps = true,
  setCurrentStep,
  refetchCart,
  refetchTickets,
  setShowSuccessMessage,
  setShowFailureMessage,
  cart,
  companyProfile,
  isRegister = false,
  organizationId,
  ticketsInCart,
  hideProceedButton = false,
}: VenueCustomerLayoutProps) {
  const { t } = useTranslation('common')
  const [showOrder, setShowOrder] = useState<boolean>(false)
  const [showInfo, setShowInfo] = useState<boolean>(false)
  const [showCampaignCode, setShowCampaignCode] = useState(false)
  const [totalSteps, setTotalSteps] = useState(0)

  useEffect(() => {
    let localTotalSteps = 0
    if (event?.sk && shouldHaveTickets) {
      localTotalSteps++
    }

    if (seasonTicket?.sk && shouldHaveTickets) {
      localTotalSteps++
    }

    if (shouldHaveRestaurant) {
      localTotalSteps++
    }

    if (shouldHaveKiosk) {
      localTotalSteps++
    }

    if (shouldHaveItems) {
      localTotalSteps++
    }

    localTotalSteps++
    setTotalSteps(localTotalSteps)
  }, [
    event,
    seasonTicket,
    shouldHaveTickets,
    shouldHaveItems,
    shouldHaveKiosk,
    shouldHaveRestaurant,
  ])

  return (
    <Layout
      venueId={
        event?.organization ??
        seasonTicket?.organization ??
        (organizationId as string) ??
        ''
      }
      user={user}
      companyProfile={companyProfile}
      organizationId={organizationId}
      hideFooter={hideFooter}
      hideHeader={true}
    >
      <Transition.Root show={showInfo} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-10"
          onClose={() => setShowInfo(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 hidden bg-gray-500 bg-opacity-75 transition-opacity md:block" />
          </Transition.Child>
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-stretch justify-center text-center md:items-center md:px-2 lg:px-4">
              {/* This element is to trick the browser into centering the modal contents. */}
              <span
                className="hidden md:inline-block md:h-screen md:align-middle"
                aria-hidden="true"
              >
                &#8203;
              </span>
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 md:translate-y-0 md:scale-95"
                enterTo="opacity-100 translate-y-0 md:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 md:scale-100"
                leaveTo="opacity-0 translate-y-4 md:translate-y-0 md:scale-95"
              >
                <Dialog.Panel className="flex w-full text-left text-base transition md:my-8 md:max-w-xl md:px-4 lg:max-w-2xl">
                  <div className="relative flex w-full flex-col items-center overflow-hidden rounded bg-white shadow-2xl">
                    <div className="w-full">
                      {seasonTicket?.image_link && (
                        <StaticImage
                          relative
                          imageLink={seasonTicket?.image_link}
                          rounded="rounded-t-lg"
                        />
                      )}
                      {event?.image_link && (
                        <StaticImage
                          relative
                          imageLink={event?.image_link}
                          rounded="rounded-t-lg"
                        />
                      )}
                    </div>
                    <div className="w-full px-4 pb-8 pt-14 shadow-2xl sm:px-6 sm:pt-8 md:p-6 lg:p-8">
                      <button
                        type="button"
                        className="absolute right-4 top-4 rounded-full bg-white p-2 text-gray-400 hover:text-gray-500 sm:right-6 sm:top-8 md:right-6 md:top-6 lg:right-8 lg:top-8"
                        onClick={() => setShowInfo(false)}
                      >
                        <span className="sr-only">{t('close')}</span>
                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                      </button>

                      <div className="grid w-full grid-cols-1 items-start gap-x-6 gap-y-8 sm:grid-cols-12 lg:items-center lg:gap-x-8">
                        <div className="col-span-12 flex h-full flex-col justify-between">
                          <div className="flex flex-col">
                            <section aria-labelledby="announcements-title">
                              {event && (
                                <div className="">
                                  <div className="p-6">
                                    <h2 className="text-lg font-extrabold text-gray-900 sm:text-xl">
                                      {event.name}
                                    </h2>
                                    <p className="py-2 text-sm font-normal text-gray-500">
                                      {event.date} | {event.time}
                                    </p>
                                  </div>
                                  <hr className="h-0.5 w-full bg-gray-100" />
                                  <div className="p-6">
                                    <h3 className="text-lg font-bold text-gray-900">
                                      {t('match-description')}
                                    </h3>
                                    <p className="whitespace-pre-wrap py-2 text-sm font-normal text-gray-500">
                                      {event.description}
                                    </p>
                                  </div>
                                  <hr className="h-0.5 w-full bg-gray-100" />
                                  <div className="p-6">
                                    <h3 className="text-lg font-bold text-gray-900">
                                      {t('arena')}
                                    </h3>
                                    <p className="py-2 text-sm font-normal text-gray-500">
                                      {arena?.name}
                                    </p>
                                  </div>
                                </div>
                              )}
                              {seasonTicket && (
                                <div className="">
                                  {seasonTicket && (
                                    <div className="p-6">
                                      <h2 className="text-lg font-extrabold text-gray-900 sm:text-xl">
                                        {seasonTicket.name}
                                      </h2>
                                      <p className="py-2 text-sm font-normal text-gray-500">
                                        {seasonTicket.season}
                                      </p>
                                    </div>
                                  )}
                                  <hr className="h-0.5 w-full bg-gray-100" />
                                  <div className="p-6">
                                    <h3 className="text-lg font-bold text-gray-900">
                                      {t('description')}
                                    </h3>
                                    <p className="whitespace-pre-wrap py-2 text-sm font-normal text-gray-500">
                                      {seasonTicket.description}
                                    </p>
                                  </div>
                                  <hr className="h-0.5 w-full bg-gray-100" />
                                  <div className="p-6">
                                    <h3 className="text-lg font-bold text-gray-900">
                                      {t('arena')}
                                    </h3>
                                    <p className="py-2 text-sm font-normal text-gray-500">
                                      {arena?.name}
                                    </p>
                                  </div>
                                </div>
                              )}
                            </section>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
      <div className="bg-white">
        <header className="relative">
          <nav aria-label={t('top') ?? ''}>
            {/* Top navigation */}

            <div
              style={{
                backgroundColor:
                  companyProfile?.primary_main && companyProfile.primary_main,
              }}
            >
              <div
                style={{
                  backgroundColor:
                    companyProfile?.primary_main && companyProfile.primary_main,
                }}
                className="bg-blue-700 py-2"
              >
                <div className="mx-2 flex items-center justify-between sm:mx-10">
                  <div className="hidden h-16 sm:block">
                    <Link
                      target="_self"
                      href={`/stores/${organizationId ?? ''}`}
                    >
                      <CompanyLogo
                        imageLink={companyProfile?.logo ?? undefined}
                      />
                    </Link>
                  </div>
                  <div className="flex h-full w-full flex-row items-center gap-x-10 text-center sm:w-auto">
                    <div className="block w-full sm:hidden">
                      <div className="flex w-full flex-row items-center justify-between pt-4 sm:hidden">
                        <div className="h-8 w-8">
                          <Link
                            target="_self"
                            href={`/stores/${organizationId ?? ''}`}
                          >
                            <CompanyLogo
                              imageLink={companyProfile?.logo ?? undefined}
                            />
                          </Link>
                        </div>
                        {event && (
                          <h1
                            className={classNames(
                              companyProfile?.primary_inverted
                                ? 'text-gray-900'
                                : 'text-white',
                              'font-inter text-wrap ml-0 w-full break-words py-2 text-center text-lg font-bold uppercase leading-tight'
                            )}
                          >
                            {formatMobileTitle(event?.name)}
                          </h1>
                        )}
                        {membership && (
                          <h1
                            className={classNames(
                              companyProfile?.primary_inverted
                                ? 'text-gray-900'
                                : 'text-white',
                              'font-inter text-wrap ml-0 w-full break-words py-2 text-center text-lg font-bold uppercase leading-tight'
                            )}
                          >
                            {membership.name}
                          </h1>
                        )}
                        {seasonTicket && (
                          <h1
                            className={classNames(
                              companyProfile?.primary_inverted
                                ? 'text-gray-900'
                                : 'text-white',
                              'font-inter text-wrap ml-0 w-full break-words py-2 text-center text-lg font-bold uppercase leading-tight'
                            )}
                          >
                            {formatTitle(seasonTicket?.name) ?? ''}
                          </h1>
                        )}
                        <div className="self-center">
                          <div
                            className="relative inline-block"
                            onClick={() => {
                              setShowOrder((prev) => !prev)
                            }}
                          >
                            <ShoppingCartIcon
                              style={{
                                stroke:
                                  companyProfile?.primary_main &&
                                  companyProfile.primary_main,
                              }}
                              className={classNames(
                                companyProfile?.primary_main === '#FFFFFF'
                                  ? 'fill-black'
                                  : 'fill-white',
                                'h-8 w-8 cursor-pointer opacity-80'
                              )}
                            ></ShoppingCartIcon>
                            {(cart?.items.length ?? 0) > 0 && (
                              <span className="absolute right-0 top-0 block h-2 w-2 -translate-y-1/2 translate-x-1/2 transform rounded-full bg-red-400 ring-2 ring-white" />
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="mt-2 flex w-full flex-row items-center justify-center sm:hidden">
                        {(event || arena) && (
                          <p
                            className={classNames(
                              companyProfile?.primary_inverted
                                ? 'text-gray-900'
                                : 'text-white',
                              'font-inter text-wrap ml-0 w-full break-words py-2 text-center text-base font-normal uppercase leading-tight'
                            )}
                          >
                            {`${event?.date ?? ''} | ${event?.time ?? ''} | ${
                              arena?.name ?? ''
                            }`}
                          </p>
                        )}
                      </div>
                      {(cart?.items.length ?? 0) > 0 &&
                        totalSteps !== currentStep && (
                          <div
                            className="flex cursor-pointer flex-row justify-center"
                            onClick={() => {
                              setShowOrder(true)
                              setShowCampaignCode(true)
                            }}
                          >
                            <p
                              className={classNames(
                                companyProfile?.primary_inverted
                                  ? 'text-gray-900'
                                  : 'text-white',
                                'block text-sm font-medium leading-6'
                              )}
                            >
                              {t('do-you-have-a-campaign-code')}
                            </p>
                            <p className="ml-1 block text-sm font-semibold leading-6 text-blue-500">
                              {t('click-here')}
                            </p>
                          </div>
                        )}
                    </div>
                    {event && (
                      <div className="hidden sm:block">
                        <h1
                          className={classNames(
                            companyProfile?.primary_inverted
                              ? 'text-gray-900'
                              : 'text-white',
                            'font-inter text-wrap ml-0 w-full break-words py-2 text-center text-lg font-bold uppercase leading-tight sm:ml-8 sm:w-auto sm:py-0 sm:text-left'
                          )}
                        >
                          {formatTitle(event?.name) ?? ''}
                        </h1>
                      </div>
                    )}
                    {seasonTicket && (
                      <div className="hidden sm:block">
                        <h1
                          className={classNames(
                            companyProfile?.primary_inverted
                              ? 'text-gray-900'
                              : 'text-white',
                            'font-inter text-wrap ml-0 w-full break-words py-2 text-center text-lg font-bold uppercase leading-tight sm:ml-8 sm:w-auto sm:py-0 sm:text-left'
                          )}
                        >
                          {formatTitle(seasonTicket?.name) ?? ''}
                        </h1>
                      </div>
                    )}
                    {event && (
                      <React.Fragment>
                        <div className="hidden sm:block">
                          <p
                            className={classNames(
                              companyProfile?.primary_inverted
                                ? 'text-gray-900'
                                : 'text-white',
                              'text-sm'
                            )}
                          >
                            {t('date')}
                          </p>
                          <h2
                            className={classNames(
                              companyProfile?.primary_inverted
                                ? 'text-gray-900'
                                : 'text-white',
                              'font-inter text-wrap break-words text-base font-bold uppercase leading-tight'
                            )}
                          >
                            {new Date(event.date ?? '').toLocaleDateString()}
                          </h2>
                        </div>
                        <div className="hidden sm:block">
                          <p
                            className={classNames(
                              companyProfile?.primary_inverted
                                ? 'text-gray-900'
                                : 'text-white',
                              'text-sm'
                            )}
                          >
                            {capitalize(t('start'))}
                          </p>
                          <h2
                            className={classNames(
                              companyProfile?.primary_inverted
                                ? 'text-gray-900'
                                : 'text-white',
                              'font-inter text-wrap break-words text-base font-bold uppercase leading-tight'
                            )}
                          >
                            {event.time}
                          </h2>
                        </div>
                      </React.Fragment>
                    )}
                    {membership && (
                      <div className="hidden sm:block">
                        <h1
                          className={classNames(
                            companyProfile?.primary_inverted
                              ? 'text-gray-900'
                              : 'text-white',
                            'font-inter text-wrap ml-0 w-full break-words py-2 text-center text-lg font-bold uppercase leading-tight sm:ml-8 sm:w-auto sm:py-0 sm:text-left'
                          )}
                        >
                          {membership.name}
                        </h1>
                      </div>
                    )}

                    {seasonTicket && (
                      <div className="hidden sm:block">
                        <p
                          className={classNames(
                            companyProfile?.primary_inverted
                              ? 'text-gray-900'
                              : 'text-white',
                            'text-sm'
                          )}
                        >
                          {t('season')}
                        </p>
                        <h2
                          className={classNames(
                            companyProfile?.primary_inverted
                              ? 'text-gray-900'
                              : 'text-white',
                            'font-inter text-wrap break-words text-base font-bold uppercase leading-tight'
                          )}
                        >
                          {seasonTicket.season}
                        </h2>
                      </div>
                    )}

                    {arena && (
                      <div className="hidden sm:block">
                        <p
                          className={classNames(
                            companyProfile?.primary_inverted
                              ? 'text-gray-900'
                              : 'text-white',
                            'text-sm'
                          )}
                        >
                          {t('arena')}
                        </p>
                        <h2
                          className={classNames(
                            companyProfile?.primary_inverted
                              ? 'text-gray-900'
                              : 'text-white',
                            'font-inter text-wrap break-words text-base font-bold uppercase leading-tight'
                          )}
                        >
                          {arena?.name}
                        </h2>
                      </div>
                    )}
                  </div>

                  <div className="mr-4 hidden flex-1 items-center justify-end gap-x-6 sm:mr-0 sm:flex">
                    {event && (
                      <button
                        type="button"
                        className={classNames(
                          companyProfile?.primary_inverted
                            ? 'border-gray-900 text-gray-900'
                            : 'border-white text-white',
                          'hover:text-primary-700 flex flex-shrink-0 items-center justify-center rounded-lg border px-3 py-2 text-sm font-medium focus:outline-none'
                        )}
                        onClick={() => setShowInfo(true)}
                      >
                        {t('match-info')}
                      </button>
                    )}
                    {seasonTicket && (
                      <button
                        type="button"
                        className={classNames(
                          companyProfile?.primary_inverted
                            ? 'border-gray-900 text-gray-900'
                            : 'border-white text-white',
                          'hover:text-primary-700 flex flex-shrink-0 items-center justify-center rounded-lg border px-3 py-2 text-sm font-medium focus:outline-none'
                        )}
                        onClick={() => setShowInfo(true)}
                      >
                        {t('season-ticket-info')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </nav>
        </header>
      </div>

      <div className="">
        {shouldShowSteps && (
          <div className="">
            <VenueLayoutSteps
              eventId={event?.sk}
              seasonTicketId={seasonTicket?.sk}
              currentStep={currentStep}
              setCurrentStep={setCurrentStep}
              shouldHaveTickets={shouldHaveTickets}
              shouldHaveItems={shouldHaveItems}
              shouldHaveKiosk={shouldHaveKiosk}
              shouldHaveRestaurant={shouldHaveRestaurant}
              setShowFailureMessage={setShowFailureMessage}
              cartItems={cart?.items}
              companyProfile={companyProfile}
            />
          </div>
        )}

        <div className="mx-auto flex flex-row flex-wrap sm:flex-nowrap">
          {shouldHaveCart ? (
            <div className="bg-gray-50 sm:w-3/4">{children}</div>
          ) : (
            <div className="w-full">{children}</div>
          )}

          {shouldHaveCart && (
            <div
              className={classNames(
                'min-h-full w-screen overflow-x-scroll sm:min-h-screen sm:w-1/4'
              )}
            >
              <VenueCart
                eventId={event?.sk}
                seasonTicketId={seasonTicket?.sk}
                organisationId={(organizationId ?? '') as string}
                currentStep={currentStep}
                setCurrentStep={setCurrentStep}
                refetchCart={refetchCart}
                refetchTickets={refetchTickets}
                setShowSuccessMessage={setShowSuccessMessage}
                setShowFailureMessage={setShowFailureMessage}
                companyProfile={companyProfile}
                shouldHaveItems={shouldHaveItems}
                shouldHaveKiosk={shouldHaveKiosk}
                shouldHaveRestaurant={shouldHaveRestaurant}
                cart={cart}
                isRegister={isRegister}
                ticketsInCart={ticketsInCart}
                showOrder={showOrder}
                setShowOrder={setShowOrder}
                showCampaignCode={showCampaignCode}
                setShowCampaignCode={setShowCampaignCode}
                hideProceedButton={hideProceedButton}
              />
            </div>
          )}
        </div>
      </div>
    </Layout>
  )
}
interface VenueLayoutProps {
  isEvents?: boolean
  isSeasonTickets?: boolean
  isCompanyPools?: boolean
  isPublicPools?: boolean
  isCRM?: boolean
  isCRMList?: boolean
  isMessages?: boolean
  isMerchandise?: boolean
  isKiosk?: boolean
  isMemberships?: boolean
  isRestaurants?: boolean
  isCampaigns?: boolean
  isTransactions?: boolean
  user: User
  children: React.ReactNode
}

type Dropdowns = {
  [key: string]: boolean
}

const VENUE_DROPDOWN_KEY = 'venue-dropdown'

function VenueLayout({
  isEvents = false,
  isCRM = false,
  isCRMList = false,
  isMessages = false,
  isCompanyPools = false,
  isPublicPools = false,
  isMerchandise = false,
  isKiosk = false,
  isMemberships = false,
  isRestaurants = false,
  isSeasonTickets = false,
  isCampaigns = false,
  isTransactions = false,
  user,
  children,
}: VenueLayoutProps) {
  const { t } = useTranslation('common')
  const { state, dispatch } = useContext(AuthContext)
  const userMenuRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [showUserMenu, setShowUserMenu] = useState<boolean>(false)

  useOutsideClick<HTMLDivElement>(userMenuRef, (event) => {
    if (showUserMenu && !buttonRef.current?.contains(event.target as Node)) {
      setShowUserMenu(false)
    }
  })

  const navigation = [
    {
      name: t('events'),
      href: '/venue/events',
      icon: CalendarDaysIcon,
      current: isEvents,
    },
  ]

  const [secondNavigation, setSecondNavigation] = useState([
    {
      id: 'tickets',
      name: t('tickets'),
      href: '/venue/events',
      icon: TicketIcon,
      openDropdown: isSeasonTickets || isPublicPools || isCompanyPools,
      children: [
        {
          name: t('season-tickets'),
          href: '/venue/season_tickets',
          current: isSeasonTickets,
        },
        {
          name: t('individual-ticket-pools'),
          href: '/venue/tickets/public_pools',
          current: isPublicPools,
        },
        {
          name: t('company-ticket-pools'),
          href: '/venue/tickets/company_pools',
          current: isCompanyPools,
        },
      ],
    },
    {
      id: 'products',
      name: t('products'),
      href: '/venue/addons',
      icon: ShoppingBagIcon,
      openDropdown: isMerchandise || isKiosk || isRestaurants || isMemberships,
      children: [
        {
          name: t('merchandise'),
          href: '/venue/addons/merch',
          current: isMerchandise,
        },
        {
          name: t('restaurant'),
          href: '/venue/addons/restaurant',
          current: isRestaurants,
        },
        {
          name: t('kiosk'),
          href: '/venue/addons/kiosk',
          current: isKiosk,
        },
        {
          name: t('memberships'),
          href: '/venue/memberships',
          current: isMemberships,
        },
      ],
    },
    {
      id: 'commerce',
      name: t('commerce'),
      icon: BuildingStorefrontIcon,
      openDropdown: isCampaigns || isTransactions,
      children: [
        {
          name: t('campaigns'),
          href: '/venue/commerce/campaigns',
          current: isCampaigns,
        },
        {
          name: t('transactions'),
          href: '/venue/commerce/transactions',
          current: isTransactions,
        },
      ],
    },
    {
      id: 'crm',
      name: 'CRM',
      href: '/venue/crm',
      icon: UsersIcon,
      openDropdown: isCRM || isMessages || isCRMList,
      children: [
        {
          name: t('lists'),
          href: '/venue/crm/lists',
          current: isCRMList,
        },
        {
          name: t('customers'),
          href: '/venue/crm/customers',
          current: isCRM,
        },
        {
          name: t('venue-layout-content'),
          href: '/venue/crm/messages',
          current: isMessages,
        },
      ],
    },
  ])

  useEffect(() => {
    const dropdown_stored = localStorage.getItem(VENUE_DROPDOWN_KEY)
    if (dropdown_stored !== null) {
      const dropdowns: Dropdowns = JSON.parse(dropdown_stored)
      setSecondNavigation((secondNavigation) =>
        secondNavigation?.map((item) => {
          return { ...item, openDropdown: dropdowns[item.id] ?? false }
        })
      )
    }
  }, [])

  const secondaryNavigation = [
    {
      name: capitalize(t('users')),
      href: '/venue/users',
      current: false,
      icon: UsersIcon,
    },
    {
      name: capitalize(t('registers')),
      href: '/venue/registers',
      current: false,
      icon: BanknotesIcon,
    },
    {
      name: capitalize(t('settings')),
      href: '/venue/settings',
      current: false,
      icon: CogIcon,
    },
    {
      name: t('arenas'),
      href: `/venue/arenas`,
      icon: BuildingOfficeIcon,
      current: false,
    },
    {
      name: t('helpdesk'),
      href: `/venue/helpdesk`,
      icon: QuestionMarkCircleIcon,
      current: false,
    },
  ]

  const thirdNavigation = [
    {
      name: t('show-store'),
      href: `/stores/${user?.organization}`,
      icon: BuildingOfficeIcon,
      current: false,
      targetBlank: true,
    },
    {
      name: t('personal-settings'),
      href: `/settings`,
      icon: CogIcon,
      current: false,
      targetBlank: false,
    },
    {
      name: capitalize(t('change-password')),
      href: `/settings/password`,
      icon: KeyIcon,
      current: false,
      targetBlank: false,
    },
  ]
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const updateNavigation = (index: number, value: boolean | undefined) => {
    if (value !== undefined) {
      const newNavigation = [...secondNavigation]
      if (newNavigation.length > index) {
        newNavigation[index]!.openDropdown = !value
        setSecondNavigation(newNavigation)
        const dropdowns: Dropdowns = {}
        newNavigation.forEach((item) => {
          dropdowns[item.id] = item.openDropdown
        })
        localStorage.setItem(VENUE_DROPDOWN_KEY, JSON.stringify(dropdowns))
      }
    }
  }

  const [organization, setOrganzation] = useState<Organization>()

  useEffect(() => {
    const fetchData = async () => {
      try {
        let response = await fetch(
          `${process.env.NEXT_PUBLIC_API_URL}/organization`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${await clientGetIdToken()}`,
            },
          }
        )
        const res: StandardAPIRequest<Organization> = await response.json()
        if (res.error) {
          throw new Error(res.error.message)
        }
        setOrganzation(res.data ?? undefined)
      } catch (error: any) {
        captureException(error)
      }
    }

    fetchData()
  }, [])

  const submitSignOut = async () => {
    try {
      await Auth.signOut()
      window.location.reload()
    } catch (error) {
      console.log('error signing out: ', error)
    }
  }

  return (
    <div className="min-w-screen min-h-screen bg-gray-50 dark:bg-gray-900">
      {organization?.company_profile === null && (
        <Link href="/venue/settings">
          <div className="flex items-center gap-x-6 bg-red-600 px-6 py-2.5 sm:px-3.5 sm:before:flex-1">
            <p className="text-sm leading-6 text-white">
              <strong className="mr-2 font-semibold">
                {t('venue-layout-missing-company-profile')}
              </strong>
              <span aria-hidden="true">&rarr;</span>
            </p>
            <div className="flex flex-1 justify-end">
              <div className="-m-3 p-3 focus-visible:outline-offset-[-4px]">
                <ArrowTopRightOnSquareIcon
                  className="h-5 w-5 text-white"
                  aria-hidden="true"
                />
              </div>
            </div>
          </div>
        </Link>
      )}
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-40 md:hidden"
          onClose={setSidebarOpen}
        >
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-600 bg-opacity-75" />
          </Transition.Child>

          <div className="fixed inset-0 z-40 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative flex w-full max-w-xs flex-1 flex-col bg-gray-50 dark:bg-gray-900">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute right-0 top-0 -mr-12 pt-2">
                    <button
                      type="button"
                      className="ml-1 flex h-10 w-10 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                      onClick={() => setSidebarOpen(false)}
                    >
                      <span className="sr-only">{t('close-sidebar')}</span>
                      <XMarkIcon
                        className="h-6 w-6 text-white"
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                </Transition.Child>
                <div className="h-0 flex-1 overflow-y-auto pb-4 pt-5">
                  <div className="flex shrink-0 items-center px-4">
                    <div className="h-8 w-auto"></div>
                  </div>
                  <nav className="mt-5 gap-y-1 px-2">
                    {navigation?.map((item) => {
                      if (item.href !== undefined) {
                        return (
                          <Link
                            key={`LINK-${item.name}`}
                            href={item.href}
                            className={classNames(
                              item.current
                                ? 'bg-gray-100 text-white dark:bg-gray-800'
                                : 'text-white hover:bg-gray-600 hover:bg-opacity-75',
                              'group flex items-center rounded-md px-2 py-2 text-base font-medium'
                            )}
                          >
                            <item.icon
                              className="mr-4 h-6 w-6 shrink-0 text-gray-300"
                              aria-hidden="true"
                            />
                            {item.name}
                          </Link>
                        )
                      } else {
                        return (
                          <h3
                            key={`HEADER-${item.name}`}
                            className="text-sm font-medium capitalize text-gray-100"
                            id="projects-headline"
                          >
                            {item.name}
                          </h3>
                        )
                      }
                    })}
                    {secondNavigation?.map((item) => {
                      if (item.href !== undefined) {
                        return (
                          <Link
                            key={`LINK-${item.name}`}
                            href={item.href}
                            className={
                              'group flex items-center rounded-md p-2 text-base font-medium text-white hover:bg-gray-600 hover:bg-opacity-75'
                            }
                          >
                            <item.icon
                              className="mr-4 h-6 w-6 shrink-0 text-gray-300"
                              aria-hidden="true"
                            />
                            {item.name}
                          </Link>
                        )
                      } else {
                        return (
                          <h3
                            key={`HEADER-${item.name}`}
                            className="text-sm font-medium capitalize text-gray-100"
                            id="projects-headline"
                          >
                            {item.name}
                          </h3>
                        )
                      }
                    })}
                  </nav>
                </div>
                <div className="flex shrink-0 border-t border-gray-800 p-4">
                  <Link href="/" className="group block shrink-0">
                    <div className="flex items-center">
                      <div className="inline-block h-10 w-10 rounded-full">
                        <ProfileImage imageLink={user.profile_image} relative />
                      </div>
                      <div className="ml-3">
                        <p className="text-base font-medium text-white">
                          {user.email}
                        </p>
                        <p className="text-sm font-medium text-gray-200 group-hover:text-white">
                          {t('back-to-home-page')}
                        </p>
                      </div>
                    </div>
                  </Link>
                </div>
              </Dialog.Panel>
            </Transition.Child>
            <div className="w-14 shrink-0" aria-hidden="true">
              {/* Force sidebar to shrink to fit close icon */}
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Static sidebar for desktop */}
      <div className="hidden md:fixed md:inset-y-0 md:flex md:w-64 md:flex-col">
        {/* Sidebar component, swap this element with another sidebar if you like */}
        <button
          data-drawer-target="sidebar-team-switch"
          data-drawer-toggle="sidebar-team-switch"
          aria-controls="sidebar-team-switch"
          type="button"
          className="ml-3 mt-2 inline-flex items-center rounded-lg p-2 text-sm text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:text-gray-400 dark:hover:bg-gray-700 dark:focus:ring-gray-600 sm:hidden"
        >
          <span className="sr-only">{t('venue-layout-open-sidebar')}</span>
          <svg
            className="h-6 w-6"
            aria-hidden="true"
            fill="currentColor"
            viewBox="0 0 20 20"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              clipRule="evenodd"
              fillRule="evenodd"
              d="M2 4.75A.75.75 0 012.75 4h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 4.75zm0 10.5a.75.75 0 01.75-.75h7.5a.75.75 0 010 1.5h-7.5a.75.75 0 01-.75-.75zM2 10a.75.75 0 01.75-.75h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 10z"
            ></path>
          </svg>
        </button>

        <aside
          id="sidebar-team-switch"
          className="fixed left-0 top-0 z-40 flex h-screen w-64 -translate-x-full flex-col transition-transform sm:translate-x-0"
          aria-label="Sidebar"
        >
          <div className="flex w-full items-center justify-between bg-gray-100 p-5 focus:outline-none focus:ring-4 focus:ring-gray-200 dark:bg-gray-800 dark:focus:ring-gray-700">
            <span className="sr-only">{t('venue-layout-open-user-menu')}</span>
            <div className="flex items-center">
              <div className="flex flex-row items-center">
                <div className="h-8">
                  <CompanyLogo
                    imageLink={organization?.company_profile?.logo ?? undefined}
                  />
                </div>
                <div className="mb-0.5 ml-2 font-semibold leading-none text-gray-900 dark:text-white">
                  {organization?.company_profile?.name}
                </div>
              </div>
            </div>
          </div>
          <div className="flex-grow overflow-y-auto bg-gray-100 px-3 pb-5 dark:bg-gray-800">
            <div
              id="dropdownCompanyName"
              className="z-10 hidden w-60 divide-y divide-gray-100 rounded bg-white shadow dark:divide-gray-600 dark:bg-gray-700"
              data-popper-placement="bottom"
            >
              <a
                href="#"
                className="flex items-center rounded px-4 pb-3 hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                <svg
                  aria-hidden="true"
                  className="h-8"
                  viewBox="0 0 54 65"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clipPath="url(#clip0_3_26)">
                    <path
                      d="M53.52 2.59375L31.52 7.98375C31.3113 8.03236 31.095 8.03882 30.8838 8.00276C30.6726 7.9667 30.4707 7.88884 30.29 7.77375L19.89 0.973752C19.6999 0.850063 19.4852 0.769031 19.2608 0.736231C19.0364 0.703432 18.8076 0.719644 18.59 0.783752L0.59 6.08375C0.424945 6.12338 0.277612 6.21644 0.170912 6.34846C0.0642122 6.48048 0.00412684 6.64406 0 6.81375L0 17.5938L18.59 12.1238C18.8076 12.0596 19.0364 12.0434 19.2608 12.0762C19.4852 12.109 19.6999 12.1901 19.89 12.3138L30.29 19.1038C30.4688 19.2232 30.6707 19.3037 30.8826 19.3398C31.0946 19.376 31.3117 19.3671 31.52 19.3138L54 13.8138V2.95375C53.9974 2.85206 53.9552 2.75541 53.8823 2.68442C53.8094 2.61343 53.7117 2.57372 53.61 2.57375L53.52 2.59375Z"
                      fill="#FF7F66"
                    />
                    <path
                      d="M40.2501 28.5637L31.5201 30.6637C31.3116 30.7146 31.0949 30.7222 30.8833 30.6861C30.6717 30.65 30.4698 30.5709 30.2901 30.4537L19.8901 23.6537C19.8486 23.6243 19.8016 23.6039 19.7519 23.5935C19.7022 23.5831 19.6509 23.5831 19.6011 23.5934C19.5514 23.6037 19.5044 23.6242 19.4629 23.6535C19.4214 23.6828 19.3864 23.7203 19.3601 23.7637C19.314 23.82 19.2892 23.8908 19.2901 23.9637V34.1937C19.2897 34.3187 19.3207 34.4418 19.38 34.5519C19.4394 34.6619 19.5254 34.7553 19.6301 34.8237L30.2901 41.8237C30.4706 41.9392 30.6728 42.0165 30.8844 42.0509C31.0959 42.0853 31.3122 42.076 31.5201 42.0237L40.7501 39.8137C42.2417 39.4535 43.5696 38.6037 44.5213 37.4C45.473 36.1962 45.9937 34.7082 46.0001 33.1737V32.9637C45.9816 31.7401 45.4785 30.5738 44.6011 29.7208C43.7237 28.8677 42.5437 28.3976 41.3201 28.4137C40.959 28.4243 40.6002 28.4746 40.2501 28.5637Z"
                      fill="#FF7F66"
                    />
                    <path
                      d="M40.25 51.2438L31.52 53.3438C31.3114 53.3924 31.0951 53.3988 30.8839 53.3628C30.6727 53.3267 30.4708 53.2489 30.29 53.1338L19.89 46.3338C19.8487 46.3054 19.8022 46.2854 19.7532 46.275C19.7041 46.2647 19.6535 46.2641 19.6042 46.2734C19.555 46.2827 19.508 46.3017 19.4661 46.3292C19.4242 46.3567 19.3882 46.3923 19.36 46.4338C19.3146 46.4943 19.29 46.568 19.29 46.6438V56.8738C19.2897 56.9988 19.3206 57.1219 19.38 57.232C19.4394 57.342 19.5253 57.4354 19.63 57.5038L30.29 64.5038C30.4688 64.6232 30.6707 64.7037 30.8827 64.7399C31.0946 64.776 31.3118 64.7672 31.52 64.7138L40.75 62.4938C42.2434 62.1369 43.5732 61.2879 44.5255 60.0834C45.4778 58.879 45.9972 57.3892 46 55.8538V55.6438C45.9816 54.4202 45.4785 53.2539 44.6011 52.4009C43.7237 51.5478 42.5437 51.0777 41.32 51.0938C40.9586 51.0995 40.5992 51.1499 40.25 51.2438Z"
                      fill="#FF7F66"
                    />
                    <path
                      opacity="0.32"
                      d="M30.8599 42.0137V30.7237C31.0776 30.7687 31.3022 30.7687 31.5199 30.7237L35.2799 29.7237L37.4799 40.5237L31.4799 41.9837C31.2777 42.0377 31.0663 42.0479 30.8599 42.0137ZM39.6799 51.3437L31.5199 53.3437C31.3022 53.3887 31.0776 53.3887 30.8599 53.3437V64.7237C31.0776 64.7687 31.3022 64.7687 31.5199 64.7237L41.8799 62.1837L39.6799 51.3437ZM30.8599 19.3437C31.0776 19.3887 31.3022 19.3887 31.5199 19.3437L33.0899 18.9637L30.8599 8.00366V19.3437Z"
                      fill="#2A344F"
                    />
                    <g opacity="0.16">
                      <path
                        opacity="0.16"
                        d="M19.29 12.0837C19.5021 12.1241 19.7053 12.202 19.89 12.3137L30.29 19.1137C30.4617 19.2278 30.6562 19.3029 30.86 19.3337V7.99365C30.6562 7.96286 30.4617 7.88776 30.29 7.77365L19.89 0.973652C19.7053 0.862007 19.5021 0.784127 19.29 0.743652V12.0837Z"
                        fill="#2A344F"
                      />
                      <path
                        opacity="0.16"
                        d="M30.86 53.3636C30.6574 53.3229 30.4642 53.2449 30.29 53.1336L19.89 46.3336C19.8038 46.2767 19.6985 46.2563 19.5973 46.2769C19.496 46.2975 19.4071 46.3575 19.35 46.4436C19.3107 46.5029 19.2898 46.5725 19.29 46.6436V56.8736C19.2897 56.9986 19.3206 57.1218 19.38 57.2318C19.4394 57.3418 19.5253 57.4353 19.63 57.5036L30.29 64.5036C30.4655 64.6102 30.6585 64.6847 30.86 64.7236V53.3636Z"
                        fill="#2A344F"
                      />
                      <path
                        opacity="0.16"
                        d="M19.89 23.6537C19.8038 23.5967 19.6985 23.5763 19.5973 23.597C19.496 23.6176 19.4071 23.6775 19.35 23.7637C19.3107 23.8229 19.2898 23.8925 19.29 23.9637V34.1937C19.2897 34.3187 19.3206 34.4418 19.38 34.5519C19.4394 34.6619 19.5253 34.7553 19.63 34.8237L30.29 41.8237C30.4639 41.9334 30.6575 42.0081 30.86 42.0437V30.7237C30.6556 30.6892 30.4611 30.6107 30.29 30.4937L19.89 23.6537Z"
                        fill="#2A344F"
                      />
                    </g>
                  </g>
                  <defs>
                    <clipPath id="clip0_3_26">
                      <rect
                        width="54"
                        height="64"
                        fill="white"
                        transform="translate(0 0.723633)"
                      />
                    </clipPath>
                  </defs>
                </svg>
                <div className="ml-3">
                  <div className="mb-0.5 font-semibold leading-none text-gray-900 dark:text-white">
                    Themesberg
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {t('venue-layout-personal-plan')}
                  </div>
                </div>
              </a>
            </div>
            <ul className="mt-0 gap-y-2">
              {navigation?.map((item, index) => (
                <li key={`${index}-${item.name}`}>
                  <Link
                    href={item.href}
                    className={classNames(
                      item.current
                        ? 'bg-gray-100  dark:bg-gray-700'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-700',
                      'group flex items-center rounded-lg p-2 text-base font-normal text-gray-900 dark:text-white'
                    )}
                  >
                    {item.icon && (
                      <item.icon
                        className="mr-3 h-6 w-6 text-gray-400 group-hover:text-gray-900 dark:group-hover:text-white"
                        aria-hidden="true"
                      />
                    )}
                    <span className="">{item.name}</span>
                  </Link>
                </li>
              ))}
              {secondNavigation?.map((item, index) => (
                <li key={`${index}-${item.name}`}>
                  {(item.children?.length ?? 0) > 0 ? (
                    <>
                      <button
                        type="button"
                        onClick={() =>
                          updateNavigation(index, item.openDropdown)
                        }
                        className={classNames(
                          'group flex w-full items-center rounded-lg p-2 text-base font-normal text-gray-900 transition duration-75 hover:bg-gray-100 dark:text-white dark:hover:bg-gray-700'
                        )}
                        aria-controls="dropdown-tasks"
                        data-collapse-toggle="dropdown-tasks"
                      >
                        {item.icon && (
                          <item.icon
                            className="mr-3 h-6 w-6 text-gray-400 group-hover:text-gray-900 dark:group-hover:text-white"
                            aria-hidden="true"
                          />
                        )}
                        <span className="flex-1 whitespace-nowrap text-left">
                          {item.name}
                        </span>
                        {item.openDropdown ? (
                          <ChevronDownIcon className="h-6 w-6" />
                        ) : (
                          <ChevronLeftIcon className="h-6 w-6" />
                        )}
                      </button>
                      <ul
                        id="dropdown-tasks"
                        className={
                          item.openDropdown ? 'gap-y-2 py-2' : 'hidden py-2'
                        }
                      >
                        {item.children?.map((child, index) => (
                          <li key={`${index}-${child.name}`}>
                            <a
                              href={child.href}
                              className={classNames(
                                child.current
                                  ? 'bg-gray-100 dark:bg-gray-700'
                                  : 'hover:bg-gray-100 dark:hover:bg-gray-700',
                                'group flex w-full items-center rounded-lg pl-11 text-base font-normal text-gray-900 transition duration-75 dark:text-white'
                              )}
                            >
                              {child.name}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : (
                    <a
                      href={item.href}
                      className="group flex items-center rounded-lg p-2 text-base font-normal text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-gray-700"
                    >
                      {item.icon && (
                        <item.icon
                          className="mr-3 h-6 w-6 text-gray-400 group-hover:text-gray-900 dark:group-hover:text-white"
                          aria-hidden="true"
                        />
                      )}
                      <span className="">{item.name}</span>
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>
          <div className="bottom-0 left-0 z-20 w-full justify-center bg-gray-100 p-4 dark:bg-gray-800">
            <ul className="mb-4 gap-y-2 border-b border-gray-200 pb-4 pl-2 dark:border-gray-700">
              {secondaryNavigation?.map((item, index) => (
                <li key={`${index}-${item.name}`}>
                  <Link
                    href={item.href}
                    className="group flex items-center text-sm text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                  >
                    <item.icon
                      className="h-4 w-4 shrink-0 text-gray-500 transition duration-75 group-hover:text-gray-900 dark:text-gray-400 dark:group-hover:text-white"
                      aria-hidden="true"
                    />
                    <span className="ml-2">{item.name}</span>
                  </Link>
                </li>
              ))}
            </ul>
            <div
              id="dropdownUserName"
              ref={userMenuRef}
              className={classNames(
                showUserMenu ? 'absolute' : 'hidden',
                'z-10 -ml-2 -mt-40 block w-60 divide-y divide-gray-100 rounded bg-white shadow dark:divide-gray-600 dark:bg-gray-700'
              )}
              data-popper-placement="top"
            >
              <ul className="my-2 gap-y-2 border-b border-gray-200 pl-2 dark:border-gray-700">
                {thirdNavigation?.map((item, index) => (
                  <li key={`${index}-${item.name}`}>
                    <Link
                      href={item.href}
                      className="group flex items-center text-sm text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                      target={item.targetBlank ? '_blank' : '_self'}
                    >
                      <item.icon
                        className="h-4 w-4 shrink-0 text-gray-500 transition duration-75 group-hover:text-gray-900 dark:text-gray-400 dark:group-hover:text-white"
                        aria-hidden="true"
                      />
                      <span className="ml-2">{item.name}</span>
                      {item.targetBlank && (
                        <ArrowTopRightOnSquareIcon className="h-3 w-3 text-gray-500 dark:text-gray-400" />
                      )}
                    </Link>
                  </li>
                ))}
                <li>
                  <div
                    className="group flex cursor-pointer items-center text-sm text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                    onClick={() => submitSignOut()}
                  >
                    <ArrowLeftOnRectangleIcon
                      className="h-4 w-4 shrink-0 text-gray-500 transition duration-75 group-hover:text-gray-900 dark:text-gray-400 dark:group-hover:text-white"
                      aria-hidden="true"
                    />
                    <span className="ml-2">{t('sign-out')}</span>
                  </div>
                </li>
              </ul>
              <div className="flex flex-row justify-between py-2">
                <Link
                  href="/"
                  className="group flex items-center pl-2 text-xs text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                  target="_blank"
                >
                  <HomeModernIcon className="h-4 w-4" aria-hidden="true" />
                  <span className="ml-2 mr-0.5">{t('go-to-liveday')}</span>
                  <ArrowTopRightOnSquareIcon className="h-3 w-3 text-gray-500 dark:text-gray-400" />
                </Link>
                <Switch
                  checked={state.theme === 'dark'}
                  onChange={(checked) => {
                    dispatch({
                      type: AuthDispatchTypes.SetColorMode,
                      payload: { colorMode: checked ? 'dark' : 'light' },
                    })
                  }}
                  className={classNames(
                    state.theme === 'dark' ? 'bg-gray-900' : 'bg-gray-200',
                    'relative mr-1 inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2'
                  )}
                >
                  <span className="sr-only">Use setting</span>
                  <span
                    className={classNames(
                      state.theme === 'dark'
                        ? 'translate-x-5'
                        : 'translate-x-0',
                      'pointer-events-none relative inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                    )}
                  >
                    <span
                      className={classNames(
                        state.theme === 'dark'
                          ? 'opacity-0 duration-100 ease-out'
                          : 'opacity-100 duration-200 ease-in',
                        'absolute inset-0 flex h-full w-full items-center justify-center transition-opacity'
                      )}
                      aria-hidden="true"
                    >
                      <svg
                        className="h-3 w-3"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
                          fillRule="evenodd"
                          clipRule="evenodd"
                        ></path>
                      </svg>
                    </span>
                    <span
                      className={classNames(
                        state.theme === 'dark'
                          ? 'opacity-100 duration-200 ease-in'
                          : 'opacity-0 duration-100 ease-out',
                        'absolute inset-0 flex h-full w-full items-center justify-center transition-opacity'
                      )}
                      aria-hidden="true"
                    >
                      <svg
                        className="h-3 w-3"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z"></path>
                      </svg>
                    </span>
                  </span>
                </Switch>
              </div>
            </div>
            <button
              className="dark:hover-bg-gray-700 my-4 flex w-full items-center justify-between rounded-lg bg-gray-100 p-2 hover:bg-gray-50 focus:outline-none focus:ring-4 focus:ring-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 dark:focus:ring-gray-700"
              type="button"
              ref={buttonRef}
              onClick={() => setShowUserMenu((userMenu) => !userMenu)}
            >
              <span className="sr-only">Open user menu</span>
              <div className="flex items-center">
                <div className="text-left">
                  <div className="mb-0.5 font-semibold leading-none text-gray-900 dark:text-white">{`${user.first_name} ${user.last_name}`}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {user.email}
                  </div>
                </div>
              </div>
              <EllipsisHorizontalIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>
        </aside>
      </div>
      <div className="flex flex-1 flex-col md:pl-64">
        <div className="sticky top-0 z-10 bg-gray-100 pl-1 pt-1 sm:pl-3 sm:pt-3 md:hidden">
          <button
            type="button"
            className="-ml-0.5 -mt-0.5 inline-flex h-12 w-12 items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-gray-500"
            onClick={() => setSidebarOpen(true)}
          >
            <span className="sr-only">{t('open-sidebar')}</span>
            <Bars3Icon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
        <main className="flex min-h-screen flex-col bg-gray-50 dark:bg-gray-900">
          <div className="flex flex-1 grow flex-col">{children}</div>
          <PoweredByLiveday />
        </main>
      </div>
    </div>
  )
}

interface AdminLayoutProps {
  isDashboard?: boolean
  isSupport?: boolean
  isUsers?: boolean
  isOrganizations?: boolean
  isCompetitions?: boolean
  isHelpdesk?: boolean
  isPress?: boolean
  isQR?: boolean
  user: User
  children: React.ReactNode
}

function AdminLayout({
  isDashboard = false,
  isSupport = false,
  isUsers = false,
  isOrganizations = false,
  isCompetitions = false,
  isHelpdesk = false,
  isPress = false,
  isQR = false,
  user,
  children,
}: AdminLayoutProps) {
  const { t } = useTranslation('common')
  const navigation = [
    {
      name: t('dashboard'),
      href: '/admin',
      icon: HomeIcon,
      current: isDashboard,
    },
    {
      name: t('support'),
      href: '/admin/support',
      icon: InboxIcon,
      current: isSupport,
    },
    {
      name: t('press'),
      href: '/admin/press',
      icon: DocumentIcon,
      current: isPress,
    },
    {
      name: t('helpdesk'),
      href: '/admin/helpdesk',
      icon: DocumentIcon,
      current: isHelpdesk,
    },
    {
      name: t('organizations'),
      href: '/admin/organizations',
      icon: UsersIcon,
      current: isOrganizations,
    },
    {
      name: t('competitions'),
      href: '/admin/competitions',
      icon: ListBulletIcon,
      current: isCompetitions,
    },
    {
      name: t('qr-codes'),
      href: '/admin/qr',
      icon: QrCodeIcon,
      current: isQR,
    },
    {
      name: t('employees'),
      href: '/admin/users',
      icon: UserIcon,
      current: isUsers,
    },
  ]
  const [sidebarOpen, setSidebarOpen] = useState(false)

  return (
    <div>
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-40 md:hidden"
          onClose={setSidebarOpen}
        >
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-600 bg-opacity-75" />
          </Transition.Child>

          <div className="fixed inset-0 z-40 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative flex w-full max-w-xs flex-1 flex-col bg-gray-50 dark:bg-gray-800">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute right-0 top-0 -mr-12 pt-2">
                    <button
                      type="button"
                      className="ml-1 flex h-10 w-10 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                      onClick={() => setSidebarOpen(false)}
                    >
                      <span className="sr-only">{t('close-sidebar')}</span>
                      <XMarkIcon
                        className="h-6 w-6 text-white"
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                </Transition.Child>
                <div className="h-0 flex-1 overflow-y-auto pb-4 pt-5">
                  <div className="flex shrink-0 items-center px-4">
                    <div className="h-8 w-auto">
                      <StaticImage
                        imageLink={'2v96za66BDQPi1aXXgABwlkLr09.png'}
                        relative
                      />
                    </div>
                  </div>
                  <nav className="mt-5 gap-y-1 px-2">
                    {navigation?.map((item) => (
                      <Link
                        key={item.name}
                        href={item.href}
                        className={classNames(
                          item.current
                            ? 'bg-gray-100 text-white dark:bg-gray-800'
                            : 'text-white hover:bg-gray-600 hover:bg-opacity-75',
                          'group flex items-center rounded-md px-2 py-2 text-base font-medium'
                        )}
                      >
                        <item.icon
                          className="mr-4 h-6 w-6 shrink-0 text-gray-300"
                          aria-hidden="true"
                        />
                        {item.name}
                      </Link>
                    ))}
                  </nav>
                </div>
                <div className="flex shrink-0 border-t border-gray-800 p-4">
                  <Link href="/" className="group block shrink-0">
                    <div className="flex items-center">
                      <div className="inline-block h-10 w-10 rounded-full">
                        <ProfileImage imageLink={user.profile_image} relative />
                      </div>
                      <div className="ml-3">
                        <p className="text-base font-medium text-white">
                          {user.email}
                        </p>
                        <p className="text-sm font-medium text-gray-200 group-hover:text-white">
                          {t('back-to-home-page')}
                        </p>
                      </div>
                    </div>
                  </Link>
                </div>
              </Dialog.Panel>
            </Transition.Child>
            <div className="w-14 shrink-0" aria-hidden="true">
              {/* Force sidebar to shrink to fit close icon */}
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Static sidebar for desktop */}
      <div className="hidden md:fixed md:inset-y-0 md:flex md:w-64 md:flex-col">
        {/* Sidebar component, swap this element with another sidebar if you like */}
        <div className="flex min-h-0 flex-1 flex-col bg-gray-50 dark:bg-gray-800">
          <div className="flex flex-1 flex-col overflow-y-auto pb-4">
            <div className="flex shrink-0 items-center">
              <div className="w-full">
                <StaticImage
                  imageLink={'2v96za66BDQPi1aXXgABwlkLr09.png'}
                  relative
                />
              </div>
            </div>
            <nav className="mt-5 flex-1 gap-y-1 px-2">
              {navigation?.map((item, index) => (
                <Link
                  key={`${index}-${item.name}`}
                  href={item.href}
                  className={classNames(
                    item.current
                      ? 'bg-gray-100  dark:bg-gray-700'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-700',
                    'group flex items-center rounded-lg p-2 text-base font-normal text-gray-900 dark:text-white'
                  )}
                >
                  <item.icon
                    className="mr-3 h-6 w-6 shrink-0 text-gray-300"
                    aria-hidden="true"
                  />
                  {item.name}
                </Link>
              ))}
            </nav>
          </div>
          <div className="flex shrink-0 border-t border-gray-800 p-4">
            <Link href="/" className="group block w-full shrink-0">
              <div className="flex items-center">
                <div className="inline-block h-9 w-9 rounded-full">
                  <ProfileImage imageLink={user.profile_image} relative />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-white">{user.email}</p>
                  <p className="text-xs font-medium text-gray-200 group-hover:text-white">
                    {t('back-to-home-page')}
                  </p>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </div>
      <div className="flex flex-1 flex-col md:pl-64">
        <div className="sticky top-0 z-10 bg-gray-100 pl-1 pt-1 sm:pl-3 sm:pt-3 md:hidden">
          <button
            type="button"
            className="-ml-0.5 -mt-0.5 inline-flex h-12 w-12 items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-gray-500"
            onClick={() => setSidebarOpen(true)}
          >
            <span className="sr-only">{t('open-sidebar')}</span>
            <Bars3Icon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
        <main className="flex-1">{children}</main>
      </div>
    </div>
  )
}

const formatTitle = (title: string | undefined): JSX.Element[] | undefined => {
  return title?.split('-')?.map((part, index) => (
    <React.Fragment key={index}>
      {index > 0 && '-'}
      {index > 0 && <br />}
      {part}
    </React.Fragment>
  ))
}

const formatMobileTitle = (title: string | undefined): string | undefined => {
  let parts = title?.split('-')
  if (parts && parts.length > 1) {
    return `vs. ${parts[1]}`
  }
  return title
}

export { Layout, VenueCustomerLayout, AdminLayout, VenueLayout, AuthLayout }
